import React, { useEffect, useState } from 'react';
import { useParams, Outlet, useLocation } from 'react-router-dom';
import { Box } from "@mui/material";
import Navigation from "../Navigation/Navigation";
import HospitalError from './HospitalError';
import styles from '../../App.module.css'; 

import { doc, getDoc, addDoc, collection, serverTimestamp } from "firebase/firestore";
import { getAnalytics, logEvent } from "firebase/analytics"; 
import { db } from "../../utils/firebase";

const HospitalLayout: React.FC = () => {
  const { hospitalId } = useParams<{ hospitalId: string }>(); // URL에서 병원명 추출
  const location = useLocation();
  const [isValid, setIsValid] = useState<boolean | null>(null);

  const logVisit = async (targetId: string) => {
    try {
      const sessionKey = `visited_${targetId}`;
      
      if (sessionStorage.getItem(sessionKey)) {
        return; 
      }

      await addDoc(collection(db, "visit_logs"), {
        hospitalId: targetId,       
        path: window.location.pathname, 
        visitedAt: serverTimestamp(),   
        userAgent: navigator.userAgent  
      });

      const analytics = getAnalytics(db.app);
      
      logEvent(analytics, 'hospital_visit', {
        hospital_id: targetId,     
        page_path: window.location.pathname,
        page_title: document.title
      });

      sessionStorage.setItem(sessionKey, 'true');
      console.log(`📊 [${targetId}] Firestore 저장 & GA 전송 완료`);

    } catch (e) {
      console.error("로그 저장/전송 중 오류 (AdBlock 확인):", e);
    }
  };

  useEffect(() => {
    const checkHospital = async () => {
      if (!hospitalId) {
        setIsValid(true); // 유효함
        logVisit('default');
        return; // DB 조회 안 하고 종료
      }

      try {
        const docRef = doc(db, "hospitals", hospitalId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          // 문서가 존재하면 -> 등록된 병원
          // const data = docSnap.data(); // 나중에 병원 이름이나 테마 색상을 가져올 때 사용
          setIsValid(true);
          logVisit(hospitalId);
        } else {
          // 문서가 없으면 -> 등록되지 않은 병원
          setIsValid(false);
        }
      } catch (error) {
        console.error("Firebase 병원 확인 실패:", error);
        setIsValid(false); // 에러 나면 안전하게 없는 병원 처리
      }
    };

    checkHospital();
  }, [hospitalId]);

  // 2. 배경화면 로직 (App.tsx에서 가져옴)
  let backgroundImage = `url(${process.env.PUBLIC_URL}/images/hospital.png)`;
  if (hospitalId === 'hana') {
    backgroundImage = `url(${process.env.PUBLIC_URL}/images/hana_hospital.jpg)`;
  } else if (hospitalId === 'samsung') {
    backgroundImage = `url(${process.env.PUBLIC_URL}/images/samsung_electronics.jpg)`;
  } else if (hospitalId === 'shinhanlifecare') {
    backgroundImage = `url(${process.env.PUBLIC_URL}/images/shinhan_lifecare.jpg)`;
  } else if (hospitalId === 'hanmiscience') {
    backgroundImage = `url(${process.env.PUBLIC_URL}/images/hanmi_science.jpg)`;
  }
  
  const backgroundClass = hospitalId ? styles[hospitalId] : styles.defaultBackground;

  // 3. 네비게이션 숨김 로직 (경로에 hospitalId가 포함되므로 로직 수정)
  // 현재 경로에서 병원ID 부분을 뺀 나머지 경로만 추출
  // 예: /hana/check -> /check
  const pathWithoutHospital = location.pathname.replace(`/${hospitalId}`, '') || '/';
  
  const hideNavigationPaths = [
    '/', // 온보딩 (병원 메인)
    '/admin',
    '/stroke/check/new',
    '/cognitive/check/new',
    '/cognitive/DigitSpan',
    '/cognitive/RandomCube',
    '/cognitive/VerbalLearning',
    '/cognitive/TowerOfLondon',
    '/headache/check/new',
    '/login',
  ];

  const isNavigationHidden = 
    pathWithoutHospital === '/' ||
    pathWithoutHospital.startsWith('/admin') ||
    pathWithoutHospital.startsWith('/stroke/report/') || 
    pathWithoutHospital.startsWith('/cognitive/report/') ||
    pathWithoutHospital.startsWith('/headache/report/') ||
    hideNavigationPaths.some(path => pathWithoutHospital === path || pathWithoutHospital.startsWith(path + '/'));

  if (isValid === null) return <div>Loading...</div>;
  if (isValid === false) return <HospitalError />;

  return (
    <Box 
      className={`${styles.container} ${backgroundClass}`} 
      style={{ backgroundImage }} 
      sx={{ minHeight: "100vh" }}
    >
      <Outlet context={{ hospitalId }} /> 
      
      {!isNavigationHidden && <Navigation />}
    </Box>
  );
};

export default HospitalLayout;